/**
 * Consolidation Page - Phase 4
 * Cross-border data consolidation with intercompany eliminations.
 * 
 * Features:
 * - Run consolidation for GHG emissions, revenue, water usage
 * - View consolidated totals with entity breakdown
 * - Intercompany eliminations tracking
 * - Data quality metrics (PCAF score)
 * - Waterfall visualization
 * - Cryptographic audit trail
 */

import React, { useState, useEffect } from 'react';
import { 
  Calculator, Database, Building2, TrendingUp, TrendingDown,
  CheckCircle, AlertTriangle, RefreshCw, Download, ChevronRight,
  Activity, BarChart3, GitMerge, Shield, Clock, Eye,
  Leaf, DollarSign, Droplets
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../services/api_service';


const API = process.env.REACT_APP_BACKEND_URL;

export default function ConsolidationPage() {
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [consolidationRun, setConsolidationRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [viewMode, setViewMode] = useState('summary');

  useEffect(() => {
    fetchRules();
    fetchRuns();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await fetch(`${API}/api/v1/consolidation/rules`);
      const data = await response.json();
      setRules(data.rules || []);
      if (data.rules?.length > 0) {
        setSelectedRule(data.rules[0]);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error('Failed to load consolidation rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await fetch(`${API}/api/v1/consolidation/runs?limit=10`);
      const data = await response.json();
      setRuns(data.runs || []);
    } catch (error) {
      console.error('Error fetching runs:', error);
    }
  };

  const runConsolidation = async () => {
    if (!selectedRule) return;
    
    try {
      setRunning(true);
      const response = await fetch(`${API}/api/v1/consolidation/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ruleId: selectedRule.ruleId,
          asOfDate: new Date().toISOString()
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'complete') {
        setConsolidationRun(data);
        toast.success('Consolidation completed successfully');
        fetchRuns();  // Refresh history
      } else {
        toast.error(data.reason || 'Consolidation failed');
      }
    } catch (error) {
      console.error('Consolidation error:', error);
      toast.error('Failed to run consolidation');
    } finally {
      setRunning(false);
    }
  };

  const loadRun = async (runId) => {
    try {
      const response = await fetch(`${API}/api/v1/consolidation/runs/${runId}`);
      const data = await response.json();
      setConsolidationRun(data);
    } catch (error) {
      toast.error('Failed to load run details');
    }
  };

  const getRuleIcon = (category) => {
    switch (category) {
      case 'emissions': return <Leaf className="h-5 w-5 text-green-400" />;
      case 'revenue': return <DollarSign className="h-5 w-5 text-yellow-400" />;
      case 'water': return <Droplets className="h-5 w-5 text-blue-400" />;
      default: return <Database className="h-5 w-5 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6" data-testid="consolidation-page">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <GitMerge className="h-7 w-7 text-purple-400" />
            Cross-Border Consolidation
          </h1>
          <p className="text-gray-400">Aggregate data across entities with intercompany eliminations</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* Left Column - Rules & History */}
        <div className="space-y-6">
          {/* Consolidation Rules */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-purple-400" />
              Consolidation Rules
            </h3>
            <div className="space-y-2">
              {rules.map((rule) => (
                <button
                  key={rule.ruleId}
                  onClick={() => setSelectedRule(rule)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    selectedRule?.ruleId === rule.ruleId
                      ? 'bg-purple-500/20 border border-purple-500/50'
                      : 'bg-gray-900/50 hover:bg-gray-800/50'
                  }`}
                  data-testid={`rule-${rule.ruleId}`}
                >
                  <div className="flex items-center gap-3">
                    {getRuleIcon(rule.dataCategory)}
                    <div>
                      <p className="text-white font-medium text-sm">{rule.ruleName}</p>
                      <p className="text-gray-500 text-xs">{rule.consolidationApproach}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {selectedRule && (
              <button
                onClick={runConsolidation}
                disabled={running}
                className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="run-consolidation-btn"
              >
                {running ? (
                  <>
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Calculator className="h-5 w-5" />
                    Run Consolidation
                  </>
                )}
              </button>
            )}
          </div>

          {/* Run History */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" />
              Run History
            </h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {runs.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No consolidation runs yet</p>
              ) : (
                runs.map((run) => (
                  <button
                    key={run.runId}
                    onClick={() => loadRun(run.runId)}
                    className={`w-full p-3 rounded-lg text-left transition-colors ${
                      consolidationRun?.runId === run.runId
                        ? 'bg-blue-500/20 border border-blue-500/50'
                        : 'bg-gray-900/50 hover:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-sm font-medium">{run.dataCategory?.toUpperCase()}</p>
                        <p className="text-gray-500 text-xs">
                          {new Date(run.runDate).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        run.status === 'complete' 
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {run.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Results */}
        <div className="col-span-3">
          {!consolidationRun ? (
            <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-12 text-center">
              <GitMerge className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Consolidation Run Selected</h3>
              <p className="text-gray-400 mb-6">
                Select a consolidation rule and click "Run Consolidation" to aggregate data across all entities.
              </p>
              {selectedRule && (
                <div className="bg-gray-900/50 rounded-lg p-4 max-w-md mx-auto text-left">
                  <p className="text-sm text-gray-400 mb-2">Selected Rule:</p>
                  <p className="text-white font-medium">{selectedRule.ruleName}</p>
                  <p className="text-gray-500 text-sm mt-1">{selectedRule.description}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {consolidationRun.dataCategory === 'emissions' && (
                  <>
                    <MetricCard
                      icon={Leaf}
                      label="Total GHG Emissions"
                      value={formatNumber(consolidationRun.consolidatedResults?.totalGHG)}
                      unit="tCO2e"
                      color="green"
                    />
                    <MetricCard
                      icon={Building2}
                      label="Entities Included"
                      value={consolidationRun.inputData?.entitiesIncluded}
                      subtitle={`${consolidationRun.inputData?.coveragePercent}% coverage`}
                      color="blue"
                    />
                    <MetricCard
                      icon={Activity}
                      label="Data Quality"
                      value={consolidationRun.dataQualityMetrics?.averagePCAFScore?.toFixed(1)}
                      subtitle="PCAF Score"
                      color="purple"
                    />
                    <MetricCard
                      icon={TrendingDown}
                      label="Eliminations"
                      value={formatNumber(consolidationRun.consolidatedResults?.totalEliminated || 0)}
                      unit="tCO2e"
                      subtitle="Intercompany"
                      color="yellow"
                    />
                  </>
                )}
                {consolidationRun.dataCategory === 'revenue' && (
                  <>
                    <MetricCard
                      icon={DollarSign}
                      label="Total Revenue"
                      value={formatCurrency(consolidationRun.consolidatedResults?.totalRevenue)}
                      color="green"
                    />
                    <MetricCard
                      icon={Building2}
                      label="Entities Included"
                      value={consolidationRun.inputData?.entitiesIncluded}
                      subtitle={`${consolidationRun.inputData?.coveragePercent}% coverage`}
                      color="blue"
                    />
                    <MetricCard
                      icon={TrendingDown}
                      label="IC Eliminations"
                      value={formatCurrency(consolidationRun.consolidatedResults?.totalEliminated || 0)}
                      color="red"
                    />
                    <MetricCard
                      icon={TrendingUp}
                      label="External Revenue"
                      value={formatCurrency(consolidationRun.consolidatedResults?.externalRevenue)}
                      color="purple"
                    />
                  </>
                )}
                {consolidationRun.dataCategory === 'water' && (
                  <>
                    <MetricCard
                      icon={Droplets}
                      label="Total Withdrawal"
                      value={formatNumber(consolidationRun.consolidatedResults?.totalWithdrawal)}
                      unit="m³"
                      color="blue"
                    />
                    <MetricCard
                      icon={Building2}
                      label="Entities Included"
                      value={consolidationRun.inputData?.entitiesIncluded}
                      subtitle={`${consolidationRun.inputData?.coveragePercent}% coverage`}
                      color="purple"
                    />
                    <MetricCard
                      icon={Activity}
                      label="Consumption"
                      value={formatNumber(consolidationRun.consolidatedResults?.totalConsumption)}
                      unit="m³"
                      color="green"
                    />
                    <MetricCard
                      icon={TrendingDown}
                      label="Discharge"
                      value={formatNumber(consolidationRun.consolidatedResults?.totalDischarge)}
                      unit="m³"
                      color="yellow"
                    />
                  </>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setViewMode('summary')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'summary'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  <BarChart3 className="h-4 w-4 inline mr-2" />
                  Summary
                </button>
                <button
                  onClick={() => setViewMode('breakdown')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'breakdown'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  <Building2 className="h-4 w-4 inline mr-2" />
                  Entity Breakdown
                </button>
                <button
                  onClick={() => setViewMode('audit')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'audit'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  <Shield className="h-4 w-4 inline mr-2" />
                  Audit Trail
                </button>
              </div>

              {/* View Content */}
              {viewMode === 'summary' && (
                <ConsolidationSummary run={consolidationRun} />
              )}
              {viewMode === 'breakdown' && (
                <EntityBreakdown run={consolidationRun} />
              )}
              {viewMode === 'audit' && (
                <AuditTrail run={consolidationRun} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper Components
function MetricCard({ icon: Icon, label, value, unit, subtitle, color }) {
  const colorClasses = {
    green: 'bg-green-500/10 text-green-400',
    blue: 'bg-blue-500/10 text-blue-400',
    purple: 'bg-purple-500/10 text-purple-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    red: 'bg-red-500/10 text-red-400'
  };
  
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-sm">{label}</span>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        <p className="text-2xl font-bold text-white">{value}</p>
        {unit && <span className="text-gray-500 text-sm">{unit}</span>}
      </div>
      {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
    </div>
  );
}

function ConsolidationSummary({ run }) {
  const results = run.consolidatedResults || {};
  const category = run.dataCategory;
  
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6" data-testid="consolidation-summary">
      <h3 className="text-lg font-semibold text-white mb-4">
        {category === 'emissions' ? 'GHG Emissions Summary' :
         category === 'revenue' ? 'Revenue Summary' :
         'Water Usage Summary'}
      </h3>
      
      {category === 'emissions' && (
        <div className="space-y-4">
          {/* Scope Breakdown Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Emissions by Scope</span>
              <span className="text-white font-medium">{formatNumber(results.totalGHG)} tCO2e total</span>
            </div>
            <div className="h-8 bg-gray-900 rounded-lg overflow-hidden flex">
              <div 
                className="bg-emerald-500 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${(results.scope1Total / results.totalGHG) * 100}%` }}
              >
                {((results.scope1Total / results.totalGHG) * 100).toFixed(0)}%
              </div>
              <div 
                className="bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${(results.scope2Total / results.totalGHG) * 100}%` }}
              >
                {((results.scope2Total / results.totalGHG) * 100).toFixed(0)}%
              </div>
              <div 
                className="bg-purple-500 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${(results.scope3Total / results.totalGHG) * 100}%` }}
              >
                {((results.scope3Total / results.totalGHG) * 100).toFixed(0)}%
              </div>
            </div>
            <div className="flex justify-between text-xs">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-emerald-500"></div>
                <span className="text-gray-400">Scope 1: {formatNumber(results.scope1Total)}</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-500"></div>
                <span className="text-gray-400">Scope 2: {formatNumber(results.scope2Total)}</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-purple-500"></div>
                <span className="text-gray-400">Scope 3: {formatNumber(results.scope3Total)}</span>
              </span>
            </div>
          </div>
          
          {/* Data Quality */}
          <div className="pt-4 border-t border-gray-800">
            <h4 className="text-white font-medium mb-3">Data Quality</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-900/50 rounded-lg p-3">
                <p className="text-gray-400 text-xs mb-1">Primary Data</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500"
                      style={{ width: `${run.dataQualityMetrics?.primaryDataPercent || 0}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium">{run.dataQualityMetrics?.primaryDataPercent}%</span>
                </div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3">
                <p className="text-gray-400 text-xs mb-1">PCAF Score</p>
                <p className="text-2xl font-bold text-white">{run.dataQualityMetrics?.averagePCAFScore?.toFixed(1)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {category === 'revenue' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-900/50 rounded-lg p-4">
              <p className="text-gray-400 text-sm">Gross Revenue</p>
              <p className="text-xl font-bold text-white">{formatCurrency(results.totalRevenueBeforeElimination)}</p>
            </div>
            <div className="bg-red-500/10 rounded-lg p-4">
              <p className="text-red-400 text-sm">IC Eliminations</p>
              <p className="text-xl font-bold text-red-300">-{formatCurrency(results.totalEliminated)}</p>
            </div>
            <div className="bg-green-500/10 rounded-lg p-4">
              <p className="text-green-400 text-sm">Net Revenue</p>
              <p className="text-xl font-bold text-green-300">{formatCurrency(results.totalRevenue)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EntityBreakdown({ run }) {
  const breakdown = run.consolidatedResults?.breakdown || [];
  const category = run.dataCategory;
  const totalGHG = run.consolidatedResults?.totalGHG || 1;
  
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6" data-testid="entity-breakdown">
      <h3 className="text-lg font-semibold text-white mb-4">Entity-by-Entity Breakdown</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-900/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Entity</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Country</th>
              {category === 'emissions' && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Scope 1</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Scope 2</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Scope 3</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Total</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">% of Group</th>
                </>
              )}
              {category === 'revenue' && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">IC Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">External</th>
                </>
              )}
              {category === 'water' && (
                <>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Withdrawal</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Consumption</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Discharge</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {breakdown
              .sort((a, b) => (b.total || b.revenue || b.waterWithdrawal || 0) - (a.total || a.revenue || a.waterWithdrawal || 0))
              .map((entity, idx) => (
                <tr key={idx} className="hover:bg-gray-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{entity.entityName}</span>
                      {entity.multiplier < 1 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300">
                          {(entity.multiplier * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{entity.country}</td>
                  {category === 'emissions' && (
                    <>
                      <td className="px-4 py-3 text-right text-gray-300">{formatNumber(entity.scope1)}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{formatNumber(entity.scope2)}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{formatNumber(entity.scope3)}</td>
                      <td className="px-4 py-3 text-right text-white font-semibold">{formatNumber(entity.total)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-purple-400 font-medium">
                          {((entity.total / totalGHG) * 100).toFixed(1)}%
                        </span>
                      </td>
                    </>
                  )}
                  {category === 'revenue' && (
                    <>
                      <td className="px-4 py-3 text-right text-white">{formatCurrency(entity.revenue)}</td>
                      <td className="px-4 py-3 text-right text-red-400">{formatCurrency(entity.intercompanySales)}</td>
                      <td className="px-4 py-3 text-right text-green-400">{formatCurrency(entity.externalSales)}</td>
                    </>
                  )}
                  {category === 'water' && (
                    <>
                      <td className="px-4 py-3 text-right text-gray-300">{formatNumber(entity.waterWithdrawal)} m³</td>
                      <td className="px-4 py-3 text-right text-gray-300">{formatNumber(entity.waterConsumption)} m³</td>
                      <td className="px-4 py-3 text-right text-gray-300">{formatNumber(entity.waterDischarge)} m³</td>
                    </>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditTrail({ run }) {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6" data-testid="audit-trail">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Shield className="h-5 w-5 text-green-400" />
        Audit Trail
      </h3>
      
      <div className="space-y-4">
        <div className="bg-gray-900/50 rounded-lg p-4">
          <p className="text-gray-400 text-sm mb-1">Run ID</p>
          <p className="text-white font-mono">{run.runId}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">Run Started</p>
            <p className="text-white">{new Date(run.auditTrail?.runStarted).toLocaleString()}</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">Run Completed</p>
            <p className="text-white">{new Date(run.auditTrail?.runCompleted).toLocaleString()}</p>
          </div>
        </div>
        
        <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
          <p className="text-green-400 text-sm mb-1">Cryptographic Hash</p>
          <p className="text-white font-mono text-sm break-all">{run.auditTrail?.cryptographicHash}</p>
        </div>
        
        {run.auditTrail?.previousRunHash && (
          <div className="bg-gray-900/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">Previous Run Hash (Chain Link)</p>
            <p className="text-white font-mono text-sm break-all">{run.auditTrail.previousRunHash}</p>
          </div>
        )}
        
        <div className="bg-gray-900/50 rounded-lg p-4">
          <p className="text-gray-400 text-sm mb-1">Consolidation Rule</p>
          <p className="text-white">{run.ruleName} ({run.ruleApplied})</p>
        </div>
        
        <div className="bg-gray-900/50 rounded-lg p-4">
          <p className="text-gray-400 text-sm mb-1">Validation Status</p>
          <div className="flex items-center gap-2">
            {run.validation?.isValid ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-green-400 font-medium">All checks passed</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                <span className="text-yellow-400 font-medium">Validation warnings</span>
              </>
            )}
          </div>
          {run.validation?.issues?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {run.validation.issues.map((issue, idx) => (
                <li key={idx} className="text-yellow-300 text-sm">• {issue}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Utility functions
function formatNumber(num) {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(0)}k`;
  return num.toLocaleString();
}

function formatCurrency(num) {
  if (!num && num !== 0) return '$0';
  if (num >= 1000000000) return `$${(num / 1000000000).toFixed(1)}B`;
  if (num >= 1000000) return `$${(num / 1000000).toFixed(0)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
  return `$${num.toLocaleString()}`;
}
